import getCookie from '../../../piano/src/helpers/get-cookie';
import * as dataLayerConstants from './constants';

// Class to track and push newsletter and registration-related events to the dataLayer
export default class DatalayerNewsletters {
	constructor () {
		// Initialize the dataLayer reference safely
		this.dataLayer = typeof window !== 'undefined' ? window.dataLayer || [] : [];
	}

	// Main entry point: determine context and push the appropriate events
	pushDatalayer ( params ) {
		const pathname = window.location.pathname; // phpcs:ignore WordPressVIPMinimum.JS.Window.VarAssignment

		if ( pathname.includes( 'registration' ) ) {
			// Track user registration flow
			params.eventType = dataLayerConstants.EVENT_TYPE_NEW_REGISTRATION_FLOW;
			params.registrationEventType = dataLayerConstants.EVENT_TYPE_DIRECT;
			this.pushRegistration( params );
		} else if ( /sign-up-thank-you/.test( pathname ) ) {
			// Track newsletter sign-up from thank-you page
			params.eventType = dataLayerConstants.EVENT_TYPE_NEWSLETTER_SIGNUP;
			params.registrationEventType = dataLayerConstants.EVENT_TYPE_NEWSLETTER_SIGNUP;
			let storedNewsletters = null;
			try {
				storedNewsletters = JSON.parse( window.localStorage.getItem( 'sign-up-newsletters' ) );
			} catch ( e ) {
				storedNewsletters = null;
			}
			if ( Array.isArray( storedNewsletters ) && storedNewsletters.length > 0 ) {
				params.selectedNewsletters = storedNewsletters;
				window.localStorage.removeItem( 'sign-up-newsletters' );
			}

			this.pushSignUp( params );
		}
	}

	// Push registration and associated newsletter sign-up
	pushRegistration ( params ) {
		const currentNewsletters = params.currentNewsletters;
		const selectedNewsletters = this.getOptedInNewslettersExcludingDefaults(
			currentNewsletters,
			this.getDefaultNewsletters(),
		);

		const eventDetails =
      selectedNewsletters.length > 0 ? 'Newsletter opted in' : 'No newsletter opted in';
		const searchParams = new URLSearchParams( encodeURI( window.location.search ) ); // phpcs:ignore WordPressVIPMinimum.JS.Window.location
		const subType = searchParams.has( dataLayerConstants.URL_PARAMETER_FORM_NAME )
			? `marketing|${searchParams.get( dataLayerConstants.URL_PARAMETER_FORM_NAME )}`
			: '';

		// Push registration event
		this.dataLayer.push( {
			event: dataLayerConstants.EVENT_NEW_REGISTRATION,
			[ dataLayerConstants.NEWSLETTER_REGISTRATION_KEY_EVENT_TYPE ]: params.registrationEventType,
			[ dataLayerConstants.NEWSLETTER_REGISTRATION_KEY_SUB_TYPE ]: subType,
			[ dataLayerConstants.NEWSLETTER_REGISTRATION_KEY_SERVICE_PLATFORM ]: dataLayerConstants.SERVICE_PLATFORM_NATIVE,
			[ dataLayerConstants.NEWSLETTER_REGISTRATION_KEY_EVENT_DETAILS ]: eventDetails,
		} );

		// Push newsletter signup event
		if ( selectedNewsletters.length > 0 ) {
			this.dataLayer.push( {
				event: dataLayerConstants.EVENT_NEWSLETTER_SIGNUP,
				[ dataLayerConstants.NEWSLETTER_SIGNUP_KEY_EVENT_ACTION ]: dataLayerConstants.EVENT_ACTION_SUBSCRIBED,
				[ dataLayerConstants.NEWSLETTER_SIGNUP_KEY_EVENT_TYPE ]: params.eventType,
				[ dataLayerConstants.NEWSLETTER_SIGNUP_KEY_OBJECT_NAME ]: selectedNewsletters.join( '|' ),
				[ dataLayerConstants.NEWSLETTER_SIGNUP_KEY_EVENT_VALUE ]: selectedNewsletters.length,
				[ dataLayerConstants.NEWSLETTER_SIGNUP_KEY_SUB_TYPE ]: subType,
			} );
		}
	}

	// Push event when user subscribes to a newsletter from a thank-you page
	pushSignUp ( params ) {
		const selectedNewsletters = params.selectedNewsletters;

		const searchParams = new URLSearchParams( encodeURI( window.location.search ) ); //phpcs:ignore WordPressVIPMinimum.JS.Window.location
		const subType = searchParams.has( dataLayerConstants.URL_PARAMETER_FORM_NAME )
			? `marketing|${searchParams.get( dataLayerConstants.URL_PARAMETER_FORM_NAME )}`
			: '';

		// Push newsletter signup event
		if ( selectedNewsletters && Array.isArray( selectedNewsletters ) && selectedNewsletters.length > 0 ) {
			this.dataLayer.push( {
				event: dataLayerConstants.EVENT_NEWSLETTER_SIGNUP,
				[ dataLayerConstants.NEWSLETTER_SIGNUP_KEY_EVENT_ACTION ]:
				dataLayerConstants.EVENT_ACTION_SUBSCRIBED,
				[ dataLayerConstants.NEWSLETTER_SIGNUP_KEY_EVENT_TYPE ]: params.eventType,
				[ dataLayerConstants.NEWSLETTER_SIGNUP_KEY_OBJECT_NAME ]:
					selectedNewsletters.join( '|' ),
				[ dataLayerConstants.NEWSLETTER_SIGNUP_KEY_EVENT_VALUE ]: selectedNewsletters.length,
				[ dataLayerConstants.NEWSLETTER_SIGNUP_KEY_SUB_TYPE ]: subType,
			} );
		}
	}

	// Push event when user unsubscribes from a newsletter (via /unsubscribe route)
	pushNewslettersUnsubscribe ( { newsletter, newsletters } ) {
		const defaultNewsletters = this.getDefaultNewsletters();
		const optedIn = this.getOptedInNewslettersExcludingDefaults( newsletters, defaultNewsletters );
		const action =
      optedIn.length === 0 ? dataLayerConstants.EVENT_ACTION_UNSUBSCRIBED : dataLayerConstants.EVENT_ACTION_UPDATED;

		const dataLayerObject = {
			event: dataLayerConstants.EVENT_NEWSLETTER_SIGNUP,
			[ dataLayerConstants.NEWSLETTER_SIGNUP_KEY_EVENT_TYPE ]: dataLayerConstants.EVENT_TYPE_NEWSLETTER_SIGNUP,
			[ dataLayerConstants.NEWSLETTER_SIGNUP_KEY_OBJECT_NAME ]: newsletter,
			[ dataLayerConstants.NEWSLETTER_SIGNUP_KEY_EVENT_VALUE ]: 1,
			[ dataLayerConstants.NEWSLETTER_SIGNUP_KEY_EVENT_ACTION ]: action,
		};

		if ( action === dataLayerConstants.EVENT_ACTION_UPDATED ) {
			dataLayerObject[ dataLayerConstants.NEWSLETTER_SIGNUP_KEY_EVENT_DETAILS ] = dataLayerConstants.EVENT_DETAILS_REMOVAL;
		}

		this.dataLayer.push( dataLayerObject );
	}

	// Return only non-default newsletters that are currently opted in
	getOptedInNewslettersExcludingDefaults ( newsletters, defaultNewsletters ) {
		return Object.keys( newsletters || {} ).filter(
			code => newsletters[ code ] && ! defaultNewsletters.includes( code ),
		);
	}

	// Retrieve default newsletters.
	getDefaultNewsletters () {
		return window.UNSUBSCRIBE_PAGE_DATA?.defaultNewsletters || [];
	}
}

// Compare two arrays for equality (ignoring order)
function arraysAreEqual ( arr1, arr2 ) {
	const set1 = new Set( arr1 );
	const set2 = new Set( arr2 );
	if ( set1.size !== set2.size ) {
		return false;
	}
	for ( const item of set1 ) {
		if ( ! set2.has( item ) ) {
			return false;
		}
	}
	return true;
}
